import React, { useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { bookList } from 'src/constants/bookList';
import { clearSession } from 'src/routes/auth/auth.actions';
import { clearUserStoredInformation } from 'src/store/user/user.actions';
import { clearFeatureFlags } from 'src/store/feature-flags/feature-flags.actions';
import { clearUserIdentification } from 'src/utils/analytics';
import LytTooltip from 'src/components/library/tooltip/LytTooltip';
import LytIcon from 'src/components/library/icon/LytIcon';
import { ICON_NAMES } from 'src/components/library/icon/icon-constants';

const LogoutContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'collapsed',
})`
    display: flex;
    flex-direction: row;
    padding: ${({ $collapsed }) => $collapsed ? '8px 0' : '8px 16px'};
    justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
    align-items: center;
    min-height: 40px;
    cursor: pointer;
    gap: 8px;
    
    &:hover {
        background-color: ${({ theme }) => theme?.components?.lyt_menu_item?.background?.hover};
    }
    
    &:active {
        background-color: ${({ theme }) => theme?.components?.lyt_menu_item?.background?.pressed};
    }
`;

const LogoutText = styled.span`
    font-size: 14px;
    line-height: 18px;
    color: ${({ theme }) => theme?.components?.lyt_menu_item?.icon?.default};
`;

const Profile = ({ collapsed }) => {
    const dispatch = useDispatch();
    const { logout } = useAuth0();
    const theme = useTheme();
    const iconColor = theme?.components?.lyt_menu_item?.icon?.default;

    /**
     * Handles the logout click event
     */
    const handleLogoutClick = useCallback(() => {
        clearUserIdentification();
        dispatch(clearSession());
        dispatch(clearUserStoredInformation());
        dispatch(clearFeatureFlags());
        toast.success('Logged out successfully', {autoClose: 1000});
        logout({ logoutParams: { returnTo: `${window.location.origin}${bookList.auth.login}` } });
    }, [dispatch, logout]);

    return (
        <LytTooltip content="Log Out" position="right">
            <LogoutContainer 
                $collapsed={collapsed}
                onClick={handleLogoutClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleLogoutClick();
                    }
                }}
            >
                <LytIcon 
                    name={ICON_NAMES.SidebarSignOut} 
                    color={iconColor}
                />
                {!collapsed && <LogoutText>Log Out</LogoutText>}
            </LogoutContainer>
        </LytTooltip>
    );
};

Profile.propTypes = {
    collapsed: PropTypes.bool,
};

export default Profile;